// @date_of_change: 2026-03-18
// CRA build-time envs only read REACT_APP_*. PUBLIC_URL is the bundle's public root.
import { getBackendBase } from '../utils/BackendUrlResolver';
import { extractCanonicalKey, canonicalKeyToUrl } from '../utils/pathNormalizer';

const ASSET_BASE  = (process.env.REACT_APP_ASSET_CDN_BASE || '').replace(/\/+$/,'');   // e.g. https://.../assets
const UPLOAD_BASE = (process.env.REACT_APP_UPLOAD_CDN_BASE || '').replace(/\/+$/,'');  // e.g. https://... (no /assets)
const PUBLIC_BASE = (process.env.PUBLIC_URL || '').replace(/\/+$/,'');

const IMG_EXTS = ['png','webp','jpg','jpeg','svg'];

const slug = s => String(s||'')
  .trim().toLowerCase()
  .replace(/[^a-z0-9]+/g,'-')
  .replace(/^-+|-+$/g,'');

const hasProto = (u) => /^https?:\/\//i.test(u) || /^data:/i.test(u);
const looksLikeFile = (s) => /\.[a-z0-9]{2,5}$/i.test(String(s||''));

function withExts(baseNoExt) {
  // returns array of base.ext candidates
  return IMG_EXTS.map(ext => `${baseNoExt}.${ext}` );
}

// Build absolute backend URL for a backend-relative path (e.g., '/placeholders/...', '/uploads/...')
// @date_of_change: 2025-10-08 - Use BackendUrlResolver for proper production URL resolution
function absoluteBackendPath(relPath) {
  try {
    if (!relPath || typeof relPath !== 'string') return relPath;
    if (!relPath.startsWith('/')) return relPath;

    // Use getBackendBase() which has production-safe fallback logic
    const backendUrl = getBackendBase();

    // getBackendBase() can return a Promise in dev mode, handle both cases
    if (backendUrl && typeof backendUrl === 'string') {
      return `${backendUrl.replace(/\/+$/,'')}${relPath}`;
    } else if (backendUrl && typeof backendUrl.then === 'function') {
      // In development mode, getBackendBase() returns a Promise
      // For synchronous callers, fall back to cached value or relative path
      const cached = typeof window !== 'undefined' && window.localStorage?.getItem('backend_url_cache');
      if (cached) {
        try {
          const { url } = JSON.parse(cached);
          if (url) return `${String(url).replace(/\/+$/,'')}${relPath}`;
        } catch (_) {}
      }
    }

    // FINAL SYNC FALLBACK: use current origin to avoid race on first load
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return `${window.location.origin}${relPath}`;
    }

    return relPath; // ultimate fallback (non-browser env)
  } catch (_) {
    return relPath;
  }
}

// Build absolute URL for uploaded assets via backend proxy
// @date_of_change: 2025-10-08 - ALWAYS use backend proxy for uploads (S3 bucket is private)
// The backend has a proxy endpoint at /uploads/* that serves assets from S3
export function normalizeUploadLikePath(relativePath) {
  try {
    if (!relativePath || typeof relativePath !== 'string') return relativePath;
    const canonicalKey = extractCanonicalKey(relativePath);
    if (!canonicalKey) return relativePath;
    return canonicalKeyToUrl(canonicalKey) || relativePath;
  } catch (_) {
    return relativePath;
  }
}

function absoluteUploadUrl(relativePath) {
  try {
    if (!relativePath || typeof relativePath !== 'string') return relativePath;
    // Normalize to '/uploads/...', including older raw S3 or proxy URLs
    const rel = normalizeUploadLikePath(relativePath);

    if (!rel.startsWith('/uploads/')) return rel;

    // ALWAYS use backend proxy for uploaded assets (S3 bucket is not publicly accessible)
    // The backend will fetch from S3 and serve with proper headers
    const abs = absoluteBackendPath(rel);
    return abs || rel;
  } catch (_) {
    return relativePath;
  }
}

function cdnOrPublic(pathUnderAssets) {
  // Accepts 'assets/...'
  const clean = String(pathUnderAssets || '').replace(/^\/+/, '');
  const tail  = clean.replace(/^assets\//, '');
  if (ASSET_BASE) return `${ASSET_BASE}/${tail}`;      // => https://.../assets/<tail>
  return `${PUBLIC_BASE}/assets/${tail}`;
}

export function resolvePlaceholderUrl(kind='token-default') {
  // @date_of_change: 2025-11-14 - Use S3 placeholder by default to match user's environment
  // rule_reference: S3_SETUP_GUIDE.md, S3_CORS_CONFIGURATION.md — absolute S3 URL avoids backend proxy requirements
  try {
    const k = String(kind || 'token-default').trim();
    const alias = (k === 'character-default' || k === 'monster-default') ? 'token-default' : k;
    const ABS_S3 = 'https://virtualcombatsimulator.s3.amazonaws.com/assets/placeholders';
    if (alias === 'token-default') {
      return `${ABS_S3}/token-default.svg`;
    }
    // Fallback for other aliases: serve from bundled assets or CDN base
    return cdnOrPublic(`assets/placeholders/${alias}.svg`);
  } catch (_) {
    return 'https://virtualcombatsimulator.s3.amazonaws.com/assets/placeholders/token-default.svg';
  }
}

export const resolveUploadedKey = async (key) => {
  if (!key) return resolvePlaceholderUrl();

  // @date_of_change: 2025-10-07 - S3-only via backend proxy
  // @date_of_change: 2025-10-09 - Direct CDN: return an absolute CDN URL when configured
  const normalizedPath = normalizeUploadLikePath(key);
  const cleanKey = String(normalizedPath || '').replace(/^\/+/, '');
  if (!cleanKey.startsWith('uploads/')) return resolvePlaceholderUrl();
  return absoluteUploadUrl('/' + cleanKey);
};

// ---- expand a single “hint” into asset paths under assets/<category>/... ----
function expandHintToKeys(hint, category) {
  if (!hint) return [];
  const s = String(hint).trim();

  // 1) Absolute URLs or data: URIs pass through untouched.
  if (hasProto(s)) return [s];

  // 2) If caller already gave us an uploads/ path (from backend), keep it as-is.
  // @date_of_change: 2025-10-06 - Added uploads/ path passthrough
  if (/^\/uploads\//i.test(s)) {
    return [s]; // Already a backend upload path, don't modify
  }
  // @date_of_change: 2025-11-20 - Accept bare 'uploads/...' (no leading slash)
  if (/^uploads\//i.test(s)) {
    const cleaned = '/' + s.replace(/^\/+/, '');
    return [cleaned];
  }

  // 3) If caller already gave us an assets/ path, keep it as-is.
  if (/^assets\//i.test(s)) {
    return looksLikeFile(s) ? [s] : withExts(s);
  }

  // 3) Raw filename like "goblin.png" or "goblin"
  if (looksLikeFile(s)) {
    // ensure lower-case name and prefix with assets/<category>/
    const fname = s.split('/').pop().toLowerCase();
    return [`assets/${category}/${fname}` ];
  }

  // 4) A bare word ("goblin", "wizard-level-1") → try all common extensions
  const base = `assets/${category}/${slug(s)}` ;
  return withExts(base);
}

// ---- character resolution ----
function characterHints(c) {
  const out = [];
  // highest priority: uploaded key (exact)
  const uploadedKey = c?.tokenAsset?.key || c?.tokenAssetKey;
  if (uploadedKey) out.push(uploadedKey);

  // next: explicit art keys (may be file names or slugs)
  // @date_of_change: 2025-10-06 - Added artwork field check (CharacterEditor saves to this field)
  if (c?.artwork)  out.push(c.artwork);
  if (c?.imageUrl) out.push(c.imageUrl);
  if (c?.artKey)   out.push(c.artKey);
  if (c?.imageKey) out.push(c.imageKey);
  if (c?.image)    out.push(c.image);
  if (c?.portrait) out.push(c.portrait);
  if (c?.icon)     out.push(c.icon);

  // semantic fallbacks
  if (c?.id)    out.push(c.id);
  if (c?.name)  out.push(c.name, c.name.split(/\s+/).pop());
  if (c?.class) out.push(c.class);

  // finally, legacy helpers that might exist in data

  return out.filter(Boolean);
}

export function resolveCharacterArtUrl(c) {
  // @date_of_change: 2025-10-07 - SINGLE SOURCE OF TRUTH
  // Following AGENTS.md principles: ONE canonical format eliminates defensive logic
  // CANONICAL FORMAT: tokenAssetKey = "uploads/characters/file.ext" (no leading slash)
  
  // 1) Check for canonical tokenAssetKey first
  const canonicalKey = c?.tokenAssetKey || c?.tokenAsset?.key;
  if (canonicalKey && typeof canonicalKey === 'string') {
    const trimmed = canonicalKey.trim();
    // Canonical format check
    if (trimmed.startsWith('uploads/')) {
      return absoluteUploadUrl('/' + trimmed);
    }
    // Legacy: handle old /uploads/ format (will be migrated away)
    if (trimmed.startsWith('/uploads/')) {
      return absoluteUploadUrl(trimmed);
    }
  }

  // 2) Fallback: attempt hint-based resolution for legacy template characters
  const hints = characterHints(c);
  for (const h of hints) {
    const keys = expandHintToKeys(h, 'characters');
    if (keys.length) {
      const k = keys[0];
      if (hasProto(k) || k.startsWith('/uploads/')) return absoluteUploadUrl(k);
      return cdnOrPublic(k);
    }
  }

  // 3) Default placeholder
  return resolvePlaceholderUrl('token-default');
}

// ---- monster resolution ----
function monsterHints(m) {
  const out = [];
  const uploadedKey = m?.tokenAsset?.key || m?.tokenAssetKey;
  if (uploadedKey) out.push(uploadedKey);

  // @date_of_change: 2025-10-06 - Added artwork field check (MonsterEditor saves to this field)
  if (m?.artwork)  out.push(m.artwork);
  if (m?.imageUrl) out.push(m.imageUrl);
  if (m?.artKey)   out.push(m.artKey);
  if (m?.imageKey) out.push(m.imageKey);
  if (m?.image)    out.push(m.image);
  if (m?.icon)     out.push(m.icon);
  if (m?.sprite)   out.push(m.sprite);

  if (m?.id)    out.push(m.id);
  if (m?.name)  out.push(m.name);
  if (m?.type)  out.push(m.type);

  return out.filter(Boolean);
}

export function resolveMonsterArtUrl(m) {
  // @date_of_change: 2025-10-07 - SINGLE SOURCE OF TRUTH
  // Following AGENTS.md principles: ONE canonical format eliminates defensive logic
  // CANONICAL FORMAT: tokenAssetKey = "uploads/monsters/file.ext" (no leading slash)
  
  // 1) Check for canonical tokenAssetKey first
  const canonicalKey = m?.tokenAssetKey || m?.tokenAsset?.key;
  if (canonicalKey && typeof canonicalKey === 'string') {
    const trimmed = canonicalKey.trim();
    // Canonical format check
    if (trimmed.startsWith('uploads/')) {
      return absoluteUploadUrl('/' + trimmed);
    }
    // Legacy: handle old /uploads/ format (will be migrated away)
    if (trimmed.startsWith('/uploads/')) {
      return absoluteUploadUrl(trimmed);
    }
  }

  // 2) Fallback: attempt hint-based resolution for legacy template monsters
  const hints = monsterHints(m);
  for (const h of hints) {
    const keys = expandHintToKeys(h, 'monsters');
    if (keys.length) {
      const k = keys[0];
      if (hasProto(k) || k.startsWith('/uploads/')) return absoluteUploadUrl(k);
      return cdnOrPublic(k);
    }
  }

  // 3) Default placeholder
  return resolvePlaceholderUrl('token-default');
}

// ---- legacy aliases (back-compat) ----
export const resolvePlaceholder = resolvePlaceholderUrl;
export function resolveCharacterArt(c){ return resolveCharacterArtUrl(c); }

// ---- tiny debug handle ----
export const __artworkEnv = { ASSET_BASE, UPLOAD_BASE, PUBLIC_BASE };
if (typeof window !== 'undefined') window.__artworkEnv = __artworkEnv;